import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import {
  Loader2, Gamepad2, ChevronDown, Layers,
  TrendingUp, ShoppingCart, Trophy,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { ModalityDrillDownModal } from '@/components/games/ModalityDrillDownModal';
import type { AdminGame, GameTheme } from '@/components/games/types';
import { MODALITIES, getModality } from '@/components/temas/modalities';
import { ThemeThumbnail } from '@/components/temas/ThemeThumbnail';
import { cn } from '@/lib/utils';

function mapApiTheme(t: any): GameTheme & { modality_code: string } {
  return {
    id: t.id,
    name: t.name ?? '',
    code: t.modalityCode ?? t.modality_code ?? t.code ?? '',
    description: t.description ?? null,
    primary_color: t.accentColor ?? t.accent_color ?? null,
    secondary_color: t.secondaryColor ?? t.secondary_color ?? null,
    background_color: t.backgroundColor ?? t.background_color ?? null,
    preview_image_url: t.previewScreenshotUrl ?? t.preview_screenshot_url ?? null,
    active: t.active ?? true,
    created_at: t.createdAt ?? t.created_at ?? '',
    modality_code: t.modalityCode ?? t.modality_code ?? '',
    theme_assets: (t.themeAssets ?? t.theme_assets ?? null) as Record<string, string> | null,
    preview_screenshot_url: t.previewScreenshotUrl ?? t.preview_screenshot_url ?? null,
  };
}

export const Route = createFileRoute('/_admin/jogos/')({
  component: JogosLandingPage,
});

// ── Biblioteca de Motores (visão alto nível) ─────────────────

interface ModalityLibraryEntry {
  modality_id: string;
  modality_code: string;
  modality_name: string;
  animation_type: string;
  games_count: number;
  active_games_count: number;
  structures_count: number;
  total_plays: number;
  total_revenue_brl: string;
  total_plays_real: number;
  total_plays_test: number;
  prizes_won_count: number;
  prizes_paid_brl: string;
  first_play_at: string | null;
  last_play_at: string | null;
  preview_cover_url: string;
  preview_banner_url: string;
}

function formatCurrencyBr(value: number | string | undefined | null) {
  const n = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(n)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function formatNumberCompact(value: number | undefined | null) {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(value);
}

const ANIMATION_LABELS: Record<string, string> = {
  scratch: 'Raspadinha',
  slot: 'Slot',
  treasure: 'Baú',
  wheel: 'Roleta',
};

function ModalidadeLibrarySection() {
  const [mode, setMode] = useState<'all' | 'real' | 'test'>('all');
  const [drillDownCode, setDrillDownCode] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<{ data: ModalityLibraryEntry[] }>({
    queryKey: ['admin-modality-library', mode],
    queryFn: async () => {
      const r = await api.get('/admin/games/modality-library', { params: { mode } });
      return r.data;
    },
    onError: () => toast.error('Erro ao carregar biblioteca de modalidades'),
  } as any);

  const entries = data?.data ?? [];

  const totalPlays = entries.reduce((s, e) => s + e.total_plays, 0);
  const totalRevenue = entries.reduce((s, e) => s + parseFloat(e.total_revenue_brl || '0'), 0);
  const totalStructures = entries.reduce((s, e) => s + e.structures_count, 0);
  const totalPrizesPaid = entries.reduce((s, e) => s + parseFloat(e.prizes_paid_brl || '0'), 0);

  const drillEntry = entries.find(e => e.modality_code === drillDownCode) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gamepad2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Biblioteca de Motores</h2>
          <span className="text-xs text-muted-foreground">
            Estatísticas agregadas por modalidade lúdica
          </span>
        </div>
        <Select value={mode} onValueChange={(v) => setMode(v as 'all' | 'real' | 'test')}>
          <SelectTrigger className="w-32 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tudo</SelectItem>
            <SelectItem value="real">Real</SelectItem>
            <SelectItem value="test">Teste</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
              <ShoppingCart className="h-3.5 w-3.5" />
              Plays Totais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumberCompact(totalPlays)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" />
              Revenue Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrencyBr(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
              <Layers className="h-3.5 w-3.5" />
              Estruturas LOTEP
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumberCompact(totalStructures)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
              <Trophy className="h-3.5 w-3.5" />
              Prêmios Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrencyBr(totalPrizesPaid)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Modality Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Erro ao carregar dados. Tente novamente.
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhuma modalidade encontrada.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {entries.map((entry) => (
            <Card key={entry.modality_code} className="overflow-hidden">
              <div className="flex">
                {entry.preview_cover_url ? (
                  <div className="w-24 flex-shrink-0 bg-muted">
                    <img
                      src={entry.preview_cover_url}
                      alt={entry.modality_name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-24 flex-shrink-0 bg-muted flex items-center justify-center">
                    <Gamepad2 className="h-8 w-8 text-muted-foreground/40" />
                  </div>
                )}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <h3 className="font-semibold text-base leading-tight">{entry.modality_name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {ANIMATION_LABELS[entry.animation_type] ?? (entry.animation_type || 'N/A')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {entry.active_games_count}/{entry.games_count} ativos
                        </span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs shrink-0"
                      onClick={() => setDrillDownCode(entry.modality_code)}
                    >
                      Detalhes
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Estruturas LOTEP</p>
                      <p className="text-sm font-semibold tabular-nums">{formatNumberCompact(entry.structures_count)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Plays</p>
                      <p className="text-sm font-semibold tabular-nums">
                        {formatNumberCompact(entry.total_plays)}
                        {entry.total_plays > 0 && (
                          <span className="text-xs font-normal text-muted-foreground ml-1">
                            ({formatNumberCompact(entry.total_plays_real)} real)
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue</p>
                      <p className="text-sm font-semibold tabular-nums">{formatCurrencyBr(entry.total_revenue_brl)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Prêmios Pagos</p>
                      <p className="text-sm font-semibold tabular-nums">{formatCurrencyBr(entry.prizes_paid_brl)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {drillEntry && (
        <ModalityDrillDownModal
          entry={drillEntry}
          open={drillDownCode !== null}
          onClose={() => setDrillDownCode(null)}
        />
      )}
    </div>
  );
}

// ── Variações por Tema ───────────────────────────────────────

interface ThemeVariationsSectionProps {
  themes: (GameTheme & { modality_code: string })[];
  games: AdminGame[];
}

function ThemeVariationsSection({ themes, games }: ThemeVariationsSectionProps) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (code: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const groups = MODALITIES.map(modality => {
    const modalityThemes = themes.filter(t => t.modality_code === modality.code);
    return { modality, themes: modalityThemes };
  }).filter(g => g.themes.length > 0);

  if (themes.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Layers className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Variações por Tema</h2>
        <span className="text-xs text-muted-foreground">({themes.length} temas)</span>
      </div>

      {groups.map(({ modality, themes: modalityThemes }) => {
        const isOpen = openGroups.has(modality.code);
        const activeCount = modalityThemes.filter(t =>
          games.some(g => g.theme_id === t.id && (g.status === 'active'))
        ).length;

        return (
          <Card key={modality.code} className="overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors text-left"
              onClick={() => toggleGroup(modality.code)}
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-sm">{modality.label}</span>
                <Badge variant="outline" className="text-xs">{modalityThemes.length} temas</Badge>
                {activeCount > 0 && (
                  <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-700">
                    {activeCount} ativo{activeCount !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
            </button>

            {isOpen && (
              <div className="border-t">
                <div className="grid gap-0 divide-y">
                  {modalityThemes.map(theme => {
                    const themeGames = games.filter(g => g.theme_id === theme.id);
                    const activeGames = themeGames.filter(g => g.status === 'active').length;
                    const themeAssets = (theme.theme_assets as Record<string, string>) ?? {};
                    const modalityConfig = getModality(modality.code);
                    return (
                      <div key={theme.id} className="flex items-center gap-4 px-4 py-3">
                        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-muted border">
                          {theme.preview_screenshot_url ? (
                            <img src={theme.preview_screenshot_url} alt={theme.name} className="w-full h-full object-cover" loading="lazy" />
                          ) : modalityConfig ? (
                            <ThemeThumbnail modality={modalityConfig} assets={themeAssets} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Gamepad2 className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{theme.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {themeGames.length} jogo{themeGames.length !== 1 ? 's' : ''} cadastrado{themeGames.length !== 1 ? 's' : ''}
                            {activeGames > 0 && ` • ${activeGames} ativo${activeGames !== 1 ? 's' : ''}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────

function JogosLandingPage() {
  const { data: games } = useQuery<AdminGame[]>({
    queryKey: ['admin-games-registered'],
    queryFn: async () => {
      const response = await api.get('/admin/games', { params: { status: 'draft,scheduled', limit: 500 } });
      const list = (response.data?.data ?? response.data ?? []) as AdminGame[];
      return list.map(game => ({
        ...game,
        modality: (game as AdminGame & { modality_info?: AdminGame['modality'] }).modality_info ?? game.modality ?? null,
      }));
    },
    staleTime: 60_000,
  });

  const { data: themesData } = useQuery({
    queryKey: ['admin-game-themes-all'],
    queryFn: async () => {
      const r = await api.get('/admin/game-themes');
      const raw: any[] = r.data?.themes ?? [];
      return raw.map(mapApiTheme);
    },
    staleTime: 60_000,
  });

  const allGames = games ?? [];
  const allThemes = themesData ?? [];

  return (
    <div className="space-y-8">
      <AdminPageHeader
        icon={Gamepad2}
        title="Jogos"
        description="Biblioteca de motores e variações por tema. Jogos são criados via Estruturas LOTEP."
      />

      <ModalidadeLibrarySection />

      <ThemeVariationsSection
        themes={allThemes}
        games={allGames}
      />
    </div>
  );
}
